import { SurveyContext } from "@/components/survey/context";
import { QuestionContext } from "@/components/survey/survey-display/question/context";
import { ax } from "@/core";
import { CanvasQuestionPageImagesWrapper } from "@/sites/personal/surveys/survey-editor/canvas/canvas-question-page/canvas-question-page-images-wrapper";
import { QuestionImage } from "@/sites/personal/surveys/survey-editor/canvas/canvas-question-page/question-image";
import type { ISurveyResponseEntry } from "@msuite/katana";
import { Grid, HStack, Spacer, VStack, useAuthContext, useUIContext } from "@msuite/picasso";
import { type FC, useContext } from "react";
import { useForm, useWatch } from "react-hook-form";
import { TbCircleDashed, TbCircleDotFilled } from "react-icons/tb";
import { Description } from "../description";
import { MultipleChoicesPossible } from "../multiple-choice-question/multiple-choices-possible";
import { Prompt } from "../prompt";
import { QuestionStepper } from "../question-stepper";
import { RequiredIndicator } from "../required-indicator";

export const ImagesQuestion: FC = () => {
	/** Context */
	const { colors } = useUIContext();
	const { user } = useAuthContext();
	const { getCurrentResponse, activeElement, survey, workspaceId, response, surveyPath } =
		useContext(SurveyContext);
	const { question } = useContext(QuestionContext);

	/** @msuite/picasso */
	const { getValues, setValue, control } = useForm<ISurveyResponseEntry>({
		defaultValues: getCurrentResponse(question) || {
			questionId: question?.id,
			sectionId: activeElement?.sectionId,
			answer: [""] as any,
			answeredBy: user?.id,
			repeatKey: activeElement?.repeatKey,
		},
	});

	const answer = useWatch({ control, name: "answer" })?.filter((a) => (a as any) !== "");
	const isValid = question?.required ? (answer?.length || 0) > 0 : true;
	const isSkippable = question?.required
		? false
		: (answer?.filter((a) => (a as any) !== "").length || 0) > 0
			? false
			: !question?.required;

	async function performUpdate() {
		try {
			await ax.patch(`/survey/${survey?.id}/workspace/${workspaceId}/response/${response?.id}`, {
				data: { values: getValues(), surveyPath },
			});
		} catch (err) {
			console.error(err);
		}
	}

	async function handleChoice(option: { id?: string; value?: string }) {
		try {
			if (question?.allowMultipleChoices) {
				let answers = [...(answer as unknown as string[])];
				if (answers.includes(option.value || "")) {
					answers = answers.filter((a) => a !== option.value);
				} else {
					answers.push(option.value || "");
				}
				setValue("answer", answers as any);
			} else {
				if (answer?.length === 0) {
					setValue("answer", [option.value as any]);
				} else {
					if (answer?.includes((option.value || "") as any)) {
						setValue("answer", []);
					} else {
						setValue("answer", [option.value as any]);
					}
				}
			}
			setTimeout(performUpdate, 100);
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<>
			<VStack
				align="stretch"
				spacing={0}
			>
				<Prompt />
				<RequiredIndicator />
			</VStack>
			<Description />
			{question?.allowMultipleChoices && <MultipleChoicesPossible />}
			<Grid
				gridTemplateColumns="repeat(5, 1fr)"
				gridAutoRows="max-content"
				gap={6}
				style={{
					overflowY: "scroll",
					paddingBottom: "100px",
					height: "100%",
					gap: "0.5rem",
				}}
			>
				{question?.images?.map((option) => {
					const isSelected = answer?.includes((option.id || "") as any);
					return (
						<VStack
							key={option.id}
							spacing={2}
							onClick={async () => {
								handleChoice({ id: option.id, value: option.id });
							}}
						>
							<CanvasQuestionPageImagesWrapper isSelected={isSelected}>
								<QuestionImage filePath={option.filePath} />
							</CanvasQuestionPageImagesWrapper>
							<HStack
								justifyContent="center"
								color={colors.gray}
								fontSize="xl"
							>
								{isSelected ? <TbCircleDotFilled /> : <TbCircleDashed />}
							</HStack>
						</VStack>
					);
				})}
			</Grid>
			<Spacer />
			<QuestionStepper
				onUpdate={performUpdate}
				isSkippable={isSkippable}
				forwardDisabled={!isValid}
			/>
		</>
	);
};
